import React, { useState, useCallback, useEffect, useRef } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { PieChartMarkers, QUALITY_COLORS, DOC_TYPE_COLORS } from '../components/Map/PieChartMarkers';
import IndividualDotLayer from '../components/Map/IndividualDotLayer';
import MapColorLegend from '../components/Map/MapColorLegend';
import FractalClusterLayer from '../components/Map/FractalClusterLayer';
import VoronoiLayer from '../components/Map/VoronoiLayer';
import SocialDiscoveryBanner from '../components/shared/SocialDiscoveryBanner';
import UltraDenseControlPanel from '../components/UltimateSearch/UltraDenseControlPanel';
import PowerUserControlPanel from '../components/common/PowerUserControlPanel';
import SearchVizControls from '../components/Search/SearchVizControls';
import SearchWindowManager from '../components/UltimateSearch/SearchWindowManager';
import CategoryTreePanel from '../components/shared/CategoryTreePanel';
import { SmallBookAd, MediumBookAd, BannerBookAd, LargeBookAd } from '../components/Ads/BookAdvertisements';
import useSearchPageData from '../hooks/useSearchPageData';
import SearchControls from '../components/UltimateSearch/SearchControls';
import SearchStatsPanel from '../components/Search/SearchStatsPanel';
import SearchResultsList from '../components/UltimateSearch/SearchResultsList';
import SearchFilteredResults from '../components/Search/SearchFilteredResults';
import SearchMapFilters from '../components/Search/SearchMapFilters';
import SearchSemanticPanel from '../components/Search/SearchSemanticPanel';
import SearchEngineResultsPanel from '../components/Search/SearchEngineResultsPanel';
import CompactResultRow from '../components/shared/CompactResultRow';
import { DataSourceToggle } from '../components/shared/DataControls';
import AISuggestions from '../components/shared/AISuggestions';
import SmartSearchSuggestions from '../components/shared/SmartSearchSuggestions';
import FilterPresets from '../components/UltimateSearch/FilterPresets';
import BatchManager from '../components/UltimateSearch/BatchManager';
import ProtocolDebugger from '../components/shared/ProtocolDebugger';
import ProtocolTemplates from '../components/shared/ProtocolTemplates';
import ProtocolSharingMarketplace from '../components/shared/ProtocolSharingMarketplace';
import ProtocolPerformanceAnalytics from '../components/shared/ProtocolPerformanceAnalytics';
import FriendsProtocolBrowser from '../components/Social/FriendsProtocolBrowser';
import BookOfTheWeekCarousel from '../components/Ads/BookOfTheWeekCarousel';
import InstallAppButton from '../components/shared/InstallAppButton';
import DiscoverTrending from '../components/shared/DiscoverTrending';
import { CreateCategoryModal, EditCategoryModal } from '../components/UltimateSearch/CategoryModals';
import ShareCategoryModal from '../components/Categories/ShareCategoryModal';
import ContentDumpModal from '../components/shared/ContentDumpModal';
import CollapsibleCategoryTree from '../components/UltimateSearch/CollapsibleCategoryTree';
import CompactChatCollabModule from '../components/UltimateSearch/CompactChatCollabModule';
import CopyButton from '../components/shared/CopyButton';

// Renders the active visualization layer inside a MapContainer
const SearchHeatmapLayer = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    const heat = L.heatLayer(points, {
      radius: 25, blur: 20, maxZoom: 10,
      gradient: { 0.2: '#2563eb', 0.4: '#FCD34D', 0.6: '#c026d3', 0.8: '#f59e0b', 1.0: '#ef4444' }
    }).addTo(map);
    return () => { map.removeLayer(heat); };
  }, [map, points]);
  return null;
};

const SearchMapLayers = ({ vizMode, results, categoryColorMap, categories, getCategoryColor, dotColorMode = 'category', legendFilter = null }) => {
  const handleMarkerClick = React.useCallback((result) => {
    if (result.url) window.open(result.url, '_blank', 'noopener,noreferrer');
  }, []);
  const noop = React.useCallback(() => {}, []);

  // Apply legend filter for individual mode
  const filteredResults = React.useMemo(() => {
    if (vizMode !== 'individual' || !legendFilter) return results;
    return results.filter(r => {
      if (dotColorMode === 'doctype') return (r.article_type || 'Unknown') === legendFilter;
      return (r.categories?.[0] || 'Uncategorized') === legendFilter;
    });
  }, [results, vizMode, legendFilter, dotColorMode]);

  if (vizMode === 'individual') {
    return <IndividualDotLayer results={filteredResults} colorMode={dotColorMode} categoryColorMap={categoryColorMap} onMarkerClick={handleMarkerClick} onMarkerHover={noop} onMarkerLeave={noop} />;
  }
  if (vizMode === 'heatmap') {
    const points = results.slice(0, 500).map(r => [r.latitude, r.longitude, 0.8]);
    return <SearchHeatmapLayer points={points} />;
  }
  if (vizMode === 'fractal') {
    return <FractalClusterLayer results={results} colorMode={dotColorMode} categoryColorMap={categoryColorMap} onMarkerClick={handleMarkerClick} onMarkerHover={noop} onMarkerLeave={noop} />;
  }
  if (vizMode === 'voronoi') {
    return <VoronoiLayer results={results} categoryColorMap={categoryColorMap} onMarkerClick={handleMarkerClick} onMarkerHover={noop} onMarkerLeave={noop} />;
  }
  // Standard: use PieChartMarkers
  return <PieChartMarkers results={results} activeModes={['category']} categoryColorMap={categoryColorMap} onMarkerClick={handleMarkerClick} onMarkerHover={noop} onMarkerLeave={noop} />;
};

// Custom colored markers for categories
const createCategoryIcon = (color) => L.divIcon({
  className: 'custom-marker',
  html: `<div style="
    background: ${color};
    width: 24px;
    height: 24px;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    border: 2px solid white;
    box-shadow: 0 2px 5px rgba(0,0,0,0.3);
  "></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  popupAnchor: [0, -24],
});

/** Create a multicolored pie-chart marker for URLs with multiple categories */
const createPieIcon = (colors) => {
  if (!colors || colors.length <= 1) return createCategoryIcon(colors?.[0] || '#FCD34D');
  const n = colors.length;
  const sliceAngle = 360 / n;
  const stops = colors.map((c, i) => `${c} ${i * sliceAngle}deg ${(i + 1) * sliceAngle}deg`).join(', ');
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background: conic-gradient(${stops});
      width: 28px; height: 28px;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.4);
    "></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });
};


const UltimateSearchPage = ({ showToast }) => {
  const d = useSearchPageData(showToast);
  // Hook data - spread into local scope
  const token = d.token, user = d.user, xhr = d.xhr, updateAnalyticsCache = d.updateAnalyticsCache, filters = d.filters;
  const { educatedIndividualFilters, setEducatedIndividualFilters, eiMinWords, setEiMinWords, minWordCount, setMinWordCount } = d;
  const { documentTypes, selectedDocTypes, setSelectedDocTypes, docTypeMinWords, setDocTypeMinWords } = d;
  const { certificationFilters, setCertificationFilters, certMinWords, setCertMinWords } = d;
  const { contentMinWords, setContentMinWords, minQualityScore, setMinQualityScore } = d;
  const { exactMatch, setExactMatch, searchDepth, setSearchDepth, aggregation, setAggregation } = d;
  const { selectedCategories, setSelectedCategories, showControlPanel, setShowControlPanel } = d;
  const { controlPanelLayout, setControlPanelLayout, filterHistory, getRecommendations } = d;
  const { loadPreferences, exportFilters, undo, redo, canUndo, canRedo } = d;
  const { categories, setCategories, searchResults, setSearchResults, searchQuery, setSearchQuery } = d;
  const { loading, categorizeLoading, showProgrammerMode, setShowProgrammerMode } = d;
  const { autoUpdateEnabled, setAutoUpdateEnabled, showCategoryModal, setShowCategoryModal } = d;
  const { newCategory, setNewCategory, editingCategory, setEditingCategory } = d;
  const { editProtocol, setEditProtocol, editCategoryName, setEditCategoryName } = d;
  const { editPrivacy, setEditPrivacy, shareCategory, setShareCategory } = d;
  const { batches, setBatches, showBatchManager, setShowBatchManager, lastBatchId, setLastBatchId } = d;
  const { showDebugger, setShowDebugger, showTemplates, setShowTemplates } = d;
  const { showMap, setShowMap, showContentDump, setShowContentDump } = d;
  const { searchWindows, setSearchWindows, addSearchWindow, closeSearchWindow } = d;
  const { esQuery, setEsQuery, esResults, setEsResults, esLoading, esTotal } = d;
  const { showSemanticSearch, setShowSemanticSearch, esSyncing, dataSource, setDataSource } = d;
  const { categoryTab, setCategoryTab, clickedCategoryResults, setClickedCategoryResults } = d;
  const { analyticsCategory, setAnalyticsCategory, draggedResult, setDraggedResult, dragOverIndex, setDragOverIndex } = d;
  const { mapCenter, setMapCenter, dedicatedMapResults, setDedicatedMapResults, mapZoom } = d;
  const { searchVizMode, setSearchVizMode, comparisonMode, setComparisonMode } = d;
  const { comparisonVizLeft, setComparisonVizLeft, comparisonVizRight, setComparisonVizRight } = d;
  const { dotColorMode, setDotColorMode, legendFilter, setLegendFilter } = d;
  const { filterInfo, setFilterInfo, refreshingLocations, locationStats } = d;
  const { showFilteredResults, setShowFilteredResults, educatedIndividualKeywords } = d;
  const { categoryColorMap, getCategoryColor, fetchCategories, fetchSearchResults, toggleCategorySelection } = d;
  const { createCategory, deleteCategory, handleSearch } = d;
  const { categorizeWithCategories, autoCategorizeSearc, aiIntelligentSearch } = d;
  const { databaseTextSearch, searchEngineAccessSearch, autoCatLoading } = d;
  const { aiSearchLoading, aiSearchMode, setAiSearchMode, dbSearchLoading, dbSearchMode, setDbSearchMode } = d;
  const { searchEngineAccessLoading, chatModulePosition, setChatModulePosition } = d;
  const { mapResults, filteredMapResults, fetchMapData, handleSemanticSearch, handleEsSync } = d;
  const { showDuplicates, setShowDuplicates, duplicates, dupLoading, loadDuplicates } = d;
  const { saveFilterState, cleaningDots, clearingDots, cleanProgress } = d;
  const { cleanMapDots, clearAllDots, refreshingDots, refreshProgress, refreshMapDots } = d;
  const clearConfirmPending = d.clearConfirmPending;
  const { handleApplyTemplate } = d;
  const { addReaction } = d;
  const windowOverlay = d.windowOverlay, setWindowOverlay = d.setWindowOverlay, plottedWindows = d.plottedWindows;
  const filteredResults = d.filteredResults;
  const toggleDocType = d.toggleDocType;
  const fetchBatches = d.fetchBatches, deleteBatch = d.deleteBatch;
  const deleteResult = d.deleteResult;
  const handleCategoryTitleClick = d.handleCategoryTitleClick;
  const clearClickedCategoryResults = d.clearClickedCategoryResults;
  const combinedClickedResults = d.combinedClickedResults;
  const orderedResults = d.orderedResults;
  const handleDragStart = d.handleDragStart, handleDragOver = d.handleDragOver;
  const handleDragLeave = d.handleDragLeave, handleDrop = d.handleDrop, handleDragEnd = d.handleDragEnd;
  const searchEngineResults = d.searchEngineResults, setSearchEngineResults = d.setSearchEngineResults;
  const saveProtocol = d.saveProtocol, cleanCategory = d.cleanCategory;

  // Ref for miniature window grid portal
  const miniatureAnchorRef = useRef(null);

  const handleEditCategory = (cat, e) => {
    e.stopPropagation();
    setEditingCategory(cat);
    setEditCategoryName(cat.name || '');
    setEditProtocol(cat.protocol || '');
    setEditPrivacy(cat.privacy || 'private');
  };

  // Build category tree for sidebar
  // Calculate result counts per category
  const getCategoryResultCount = (categoryId) => {
    return searchResults.filter(r => 
      r.category_ids?.includes(categoryId) || 
      r.categories?.some(c => categories.find(cat => cat.name === c)?.id === categoryId)
    ).length;
  };

  const buildCategoryTree = (cats, parentId = null, level = 0) => {
    return cats
      .filter(c => c.parent_id === parentId)
      .map(cat => {
        const resultCount = getCategoryResultCount(cat.id);
        return (
        <div key={cat.id}>
          <div 
            className={`category-item category-item-level-${level} ${selectedCategories.includes(cat.id) ? 'selected' : ''}`}
            style={{ 
              flexDirection: 'column', 
              alignItems: 'stretch',
              borderLeft: selectedCategories.includes(cat.id) ? `3px solid ${getCategoryColor(cat.id)}` : '3px solid transparent',
              transition: 'all 0.2s'
            }}
          >
            <div 
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', flexWrap: 'nowrap', gap: 8 }}
              onClick={() => toggleCategorySelection(cat.id)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(cat.id)}
                  onChange={() => {}}
                  style={{ accentColor: getCategoryColor(cat.id), width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
                  data-testid={`category-checkbox-${cat.id}`}
                />
                <span style={{ 
                  color: selectedCategories.includes(cat.id) ? '#FEF08A' : '#e2e8f0',
                  fontWeight: selectedCategories.includes(cat.id) ? 600 : 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {cat.name}
                  {/* Result count in parentheses */}
                  <span style={{ 
                    color: resultCount > 0 ? '#10b981' : '#71717a',
                    fontSize: '0.75rem',
                    marginLeft: 6,
                    fontWeight: 400
                  }}>
                    ({resultCount})
                  </span>
                </span>
              </div>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
                <CopyButton text={cat.name} label="" successLabel="✓" size="sm" variant="icon" showToast={showToast} style={{ fontSize: '0.7rem' }} />
                {cat.protocol && (
                  <CopyButton text={cat.protocol} label="" successLabel="✓" size="sm" variant="icon" showToast={showToast} style={{ fontSize: '0.7rem', color: '#047857' }} />
                )}
                {cat.is_public && <span style={{ fontSize: '0.6rem', color: '#10b981', padding: '1px 4px', background: 'rgba(16,185,129,0.2)', borderRadius: 4, flexShrink: 0 }}>Pub</span>}
                <button 
                  onClick={(e) => { e.stopPropagation(); setShareCategory(cat); }}
                  style={{ background: 'transparent', border: 'none', color: '#3b82f6', cursor: 'pointer', padding: '2px 4px', fontSize: '0.7rem', flexShrink: 0 }}
                  title="Share Category"
                  data-testid={`share-category-${cat.id}`}
                >
                  🔗
                </button>
                <button 
                  onClick={(e) => handleEditCategory(cat, e)}
                  style={{ background: 'transparent', border: 'none', color: '#a1a1aa', cursor: 'pointer', padding: '2px 4px', fontSize: '0.7rem', flexShrink: 0 }}
                  title="Edit Protocol"
                  data-testid={`edit-category-${cat.id}`}
                >
                  <i className="fas fa-pen" style={{ fontSize: '0.6rem' }} />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); setAnalyticsCategory({ id: cat.id, name: cat.name }); }}
                  style={{ background: 'transparent', border: 'none', color: '#FDE047', cursor: 'pointer', padding: '2px 4px', fontSize: '0.7rem', flexShrink: 0 }}
                  title="View Protocol Performance Analytics"
                  data-testid={`analytics-category-${cat.id}`}
                >
                  <i className="fas fa-chart-bar" style={{ fontSize: '0.6rem' }} />
                </button>
              </div>
            </div>
            {cat.protocol && (
              <div style={{ fontSize: '0.7rem', color: '#71717a', marginTop: 4, padding: '4px 8px', background: 'rgba(252, 211, 77, 0.1)', borderRadius: 4, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {cat.protocol.length > 60 ? cat.protocol.substring(0, 60) + '...' : cat.protocol}
              </div>
            )}
          </div>
          {buildCategoryTree(cats, cat.id, level + 1)}
        </div>
      )});
  };
  return (
    <div>
      {/* Chat Module - position: top_of_page */}
      {chatModulePosition === 'top_of_page' && <CompactChatCollabModule showToast={showToast} />}

      {/* Ultra-Dense Control Panel */}
      <UltraDenseControlPanel
        show={showControlPanel} onClose={() => setShowControlPanel(false)}
        layout={controlPanelLayout} setLayout={setControlPanelLayout}
        educatedIndividualFilters={educatedIndividualFilters} setEducatedIndividualFilters={setEducatedIndividualFilters}
        eiMinWords={eiMinWords} setEiMinWords={setEiMinWords}
        selectedDocTypes={selectedDocTypes} setSelectedDocTypes={setSelectedDocTypes} documentTypes={documentTypes}
        docTypeMinWords={docTypeMinWords} setDocTypeMinWords={setDocTypeMinWords}
        certificationFilters={certificationFilters} setCertificationFilters={setCertificationFilters}
        certMinWords={certMinWords} setCertMinWords={setCertMinWords}
        minQualityScore={minQualityScore} setMinQualityScore={setMinQualityScore}
        minWordCount={minWordCount} setMinWordCount={setMinWordCount}
        exactMatch={exactMatch} setExactMatch={setExactMatch}
        searchDepth={searchDepth} setSearchDepth={setSearchDepth}
        aggregation={aggregation} setAggregation={setAggregation}
        selectedCategories={selectedCategories} setSelectedCategories={setSelectedCategories} categories={categories}
        contentMinWords={contentMinWords} setContentMinWords={setContentMinWords}
        filterHistory={filterHistory} getRecommendations={getRecommendations}
        showToast={showToast}
      />
      <PowerUserControlPanel show={showProgrammerMode} onClose={() => setShowProgrammerMode(false)} />
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <h2>{user?.username}&apos;s Ultimate Search Page</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={undo} disabled={!canUndo} data-testid="undo-btn"
              className="btn-ocean" title="Undo last filter change (Ctrl+Z)"
              style={{ background: canUndo ? 'rgba(245,158,11,0.12)' : 'rgba(255, 255, 255, 0.85)', border: `1px solid ${canUndo ? 'rgba(245,158,11,0.3)' : 'rgba(203, 213, 225, 0.8)'}`, color: canUndo ? '#f59e0b' : '#555', padding: '7px 10px', borderRadius: 8, cursor: canUndo ? 'pointer' : 'default', fontSize: '0.74rem', fontWeight: 700, fontFamily: '"SF Mono", monospace' }}>
              <i className="fas fa-undo" />
            </button>
            <button onClick={redo} disabled={!canRedo} data-testid="redo-btn"
              className="btn-ocean" title="Redo filter change (Ctrl+Y)"
              style={{ background: canRedo ? 'rgba(16,185,129,0.12)' : 'rgba(255, 255, 255, 0.85)', border: `1px solid ${canRedo ? 'rgba(16,185,129,0.3)' : 'rgba(203, 213, 225, 0.8)'}`, color: canRedo ? '#10b981' : '#555', padding: '7px 10px', borderRadius: 8, cursor: canRedo ? 'pointer' : 'default', fontSize: '0.74rem', fontWeight: 700, fontFamily: '"SF Mono", monospace' }}>
              <i className="fas fa-redo" />
            </button>
            <button onClick={() => setShowControlPanel(true)} data-testid="open-control-panel-btn"
              className="btn-ocean ocean-shimmer"
              style={{ background: 'linear-gradient(135deg, rgba(253, 230, 138,0.12), rgba(252, 211, 77,0.08))', border: '1px solid rgba(253, 230, 138,0.3)', color: '#38bdf8', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.74rem', fontWeight: 700, fontFamily: '"SF Mono", monospace', letterSpacing: 0.8, position: 'relative', overflow: 'hidden' }}>
              [CTRL]
            </button>
            <button onClick={() => setShowProgrammerMode(true)} data-testid="open-programmer-mode-btn"
              className="btn-ocean"
              style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(253, 230, 138,0.08))', border: '1px solid rgba(16,185,129,0.3)', color: '#10b981', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.74rem', fontWeight: 700, fontFamily: '"SF Mono", monospace', letterSpacing: 0.8 }}>
              [PRO]
            </button>
            <button className="btn btn-primary" onClick={() => setShowCategoryModal(true)}>
              <i className="fas fa-plus" style={{ marginRight: 4 }} /> New Category
            </button>
            <button
              data-testid="dump-content-btn"
              onClick={() => setShowContentDump(true)}
              style={{
                background: 'linear-gradient(135deg, rgba(253, 230, 138,0.12), rgba(252, 211, 77,0.08))',
                border: '1px solid rgba(253, 230, 138,0.3)', color: '#047857',
                padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                fontSize: '0.74rem', fontWeight: 700, fontFamily: '"SF Mono", monospace'
              }}
            >
              <i className="fas fa-file-alt" style={{ marginRight: 4 }} /> Dump
            </button>
          </div>
        </div>

        {/* Quick Categories Navigation - Top */}
        <div data-testid="top-categories-nav" style={{
          marginBottom: 12, padding: '8px 10px',
          background: '#475569', borderRadius: 10,
          border: '1px solid rgba(253, 224, 71, 0.15)', maxHeight: 120, overflowY: 'auto'
        }}>
          <div style={{ fontSize: '0.58rem', color: '#047857', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
            Quick Category Filter ({categories.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {categories.map(cat => (
              <button
                key={cat.id}
                data-testid={`top-cat-${cat.id}`}
                onClick={() => {
                  setSelectedCategories(prev =>
                    prev.includes(cat.id) ? prev.filter(c => c !== cat.id) : [...prev, cat.id]
                  );
                }}
                style={{
                  padding: '2px 7px', borderRadius: 4, cursor: 'pointer',
                  fontSize: '0.56rem', fontWeight: 500, lineHeight: 1.3,
                  border: `1px solid ${selectedCategories.includes(cat.id) ? 'rgba(244,114,182,0.5)' : 'rgba(203, 213, 225, 0.8)'}`,
                  background: selectedCategories.includes(cat.id) ? 'rgba(244,114,182,0.15)' : 'rgba(255, 255, 255, 0.85)',
                  color: selectedCategories.includes(cat.id) ? '#FEF08A' : '#a1a1aa',
                  transition: 'all 0.15s'
                }}
              >
                {cat.name}
              </button>
            ))}
            {categories.length > 0 && (
              <button
                data-testid="top-cat-clear-all"
                onClick={() => setSelectedCategories([])}
                style={{
                  padding: '2px 7px', borderRadius: 4, cursor: 'pointer',
                  fontSize: '0.56rem', fontWeight: 600,
                  border: '1px solid rgba(239,68,68,0.3)',
                  background: 'rgba(239,68,68,0.08)', color: '#f87171'
                }}
              >
                Clear All
              </button>
            )}
          </div>
        </div>

        {/* Search Controls Component */}
        <SearchControls
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onSearch={handleSearch}
          onCategorize={categorizeWithCategories}
          onAutoCategorize={autoCategorizeSearc}
          onAISearch={aiIntelligentSearch}
          onDatabaseSearch={databaseTextSearch}
          onSearchEngineAccess={searchEngineAccessSearch}
          loading={loading}
          categorizeLoading={categorizeLoading}
          autoCatLoading={autoCatLoading}
          aiSearchLoading={aiSearchLoading}
          dbSearchLoading={dbSearchLoading}
          searchEngineAccessLoading={searchEngineAccessLoading}
          selectedCategoriesCount={selectedCategories.length}
          aggregation={aggregation}
          setAggregation={setAggregation}
          aiSearchMode={aiSearchMode}
          setAiSearchMode={setAiSearchMode}
          dbSearchMode={dbSearchMode}
          setDbSearchMode={setDbSearchMode}
          showMap={showMap}
          setShowMap={setShowMap}
          mapResultsCount={mapResults.length}
          showDebugger={showDebugger}
          setShowDebugger={setShowDebugger}
          showTemplates={showTemplates}
          setShowTemplates={setShowTemplates}
          filterInfo={filterInfo}
          certificationFilters={certificationFilters}
          setCertificationFilters={setCertificationFilters}
          educatedIndividualFilters={educatedIndividualFilters}
          setEducatedIndividualFilters={setEducatedIndividualFilters}
          minWordCount={minWordCount}
          setMinWordCount={setMinWordCount}
          eiMinWords={eiMinWords}
          setEiMinWords={setEiMinWords}
          exactMatch={exactMatch}
          setExactMatch={setExactMatch}
          searchDepth={searchDepth}
          setSearchDepth={setSearchDepth}
        />

        {/* Miniature Window Grid anchor — portal target from SearchWindowManager */}
        <div ref={miniatureAnchorRef} data-testid="miniature-anchor" />

        {/* Smart Search Suggestions - AI-powered */}
        <SmartSearchSuggestions 
          query={searchQuery}
          onSuggestionClick={(suggestion) => setSearchQuery(suggestion)}
          showToast={showToast}
        />

        {/* Protocol Debugger */}
        {showDebugger && <ProtocolDebugger showToast={showToast} />}
        
        {/* Protocol Templates */}
        {showTemplates && <ProtocolTemplates showToast={showToast} onApplyTemplate={handleApplyTemplate} />}

        {/* Filter Presets */}
        <FilterPresets
          currentFilters={{
            selectedDocTypes,
            certificationFilters,
            educatedIndividualFilters,
            minWordCount,
            minQualityScore,
            exactMatch,
            searchDepth,
            aggregation
          }}
          onApplyPreset={(filters) => {
            if (filters.selectedDocTypes) setSelectedDocTypes(filters.selectedDocTypes);
            if (filters.certificationFilters) setCertificationFilters(filters.certificationFilters);
            if (filters.educatedIndividualFilters) setEducatedIndividualFilters(filters.educatedIndividualFilters);
            if (filters.minWordCount !== undefined) setMinWordCount(filters.minWordCount);
            if (filters.minQualityScore !== undefined) setMinQualityScore(filters.minQualityScore);
            if (filters.exactMatch !== undefined) setExactMatch(filters.exactMatch);
            if (filters.searchDepth !== undefined) setSearchDepth(filters.searchDepth);
            if (filters.aggregation) setAggregation(filters.aggregation);
            showToast('Filter preset applied!', 'success');
          }}
          showToast={showToast}
        />
      </div>

      {/* AI-Powered Protocol Suggestions */}
      <AISuggestions 
        showToast={showToast} 
        onViewProtocol={(protocolId) => {
          // Open the marketplace page via sidebar navigation
          const sidebarLink = document.querySelector('[data-testid="nav-marketplace"], [href="#marketplace"]');
          if (sidebarLink) {
            sidebarLink.click();
          } else {
            // Fallback: show protocol details in a toast with copy action
            const xhr = new XMLHttpRequest();
            xhr.open('GET', `${API}/marketplace/protocol/${protocolId}`, true);
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.onload = function() {
              if (xhr.status === 200) {
                try {
                  const data = JSON.parse(xhr.responseText);
                  showToast(`Protocol: ${data.name || 'Unknown'} — ${data.description || data.protocol?.substring(0, 100) || 'No description'}. Check the Marketplace page for full details.`, 'success');
                } catch { showToast('Opening protocol details...', 'info'); }
              } else {
                showToast('Protocol details unavailable. Visit the Marketplace page to browse.', 'info');
              }
            };
            xhr.onerror = function() { showToast('Could not load protocol. Visit the Marketplace page.', 'info'); };
            xhr.send();
          }
        }}
      />

      {/* Strategic Book Ad between controls and data */}
      <BannerBookAd variant={1} />

      {/* Data Source Toggle */}
      <div className="card" style={{ marginBottom: 15, padding: '12px 20px' }}>
        <DataSourceToggle 
          dataSource={dataSource}
          setDataSource={setDataSource}
          personalCount={dataSource === 'personal' ? mapResults.length : 0}
          worldwideCount={dataSource === 'worldwide' ? mapResults.length : 0}
        />
      </div>

      {/* Interactive Map - Collapsible */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: showMap ? '0 0 15px 0' : 0 }}>
          <button onClick={() => setShowMap(v => !v)} data-testid="toggle-map-btn"
            style={{ background: showMap ? 'rgba(244,114,182,0.12)' : 'rgba(255, 255, 255, 0.85)', border: `1px solid ${showMap ? 'rgba(244,114,182,0.3)' : 'rgba(255,255,255,0.1)'}`, color: showMap ? '#FEF08A' : '#a1a1aa', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className={`fas fa-${showMap ? 'chevron-up' : 'chevron-down'}`} />
            <i className="fas fa-map-marked-alt" style={{ marginRight: 2 }} />
            {showMap ? 'Collapse Map' : 'Expand Map'} ({filteredMapResults.length} locations)
          </button>
          {showMap && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {/* Refresh Map Dots Button */}
              <button
                onClick={refreshMapDots}
                disabled={refreshingDots}
                data-testid="refresh-map-dots-btn"
                style={{
                  padding: '6px 14px',
                  fontSize: '0.75rem',
                  background: refreshingDots ? 'rgba(245, 158, 11, 0.3)' : 'rgba(16, 185, 129, 0.2)',
                  border: `1px solid ${refreshingDots ? 'rgba(245,158,11,0.5)' : 'rgba(16, 185, 129, 0.4)'}`,
                  borderRadius: 6,
                  color: refreshingDots ? '#f59e0b' : '#10b981',
                  cursor: refreshingDots ? 'wait' : 'pointer',
                  fontWeight: 600
                }}
              >
                {refreshingDots ? (
                  <><i className="fas fa-sync-alt fa-spin" style={{ marginRight: 4 }} /> Refreshing...</>
                ) : (
                  <><i className="fas fa-map-pin" style={{ marginRight: 4 }} /> Refresh Map Dots</>
                )}
              </button>
              {/* Clean Map Dots Button — re-verify against protocols */}
              <button
                onClick={cleanMapDots}
                disabled={cleaningDots || clearingDots}
                data-testid="clean-map-dots-btn"
                style={{
                  padding: '6px 14px',
                  fontSize: '0.75rem',
                  background: cleaningDots ? 'rgba(245, 158, 11, 0.3)' : 'rgba(245, 158, 11, 0.2)',
                  border: `1px solid ${cleaningDots ? 'rgba(245,158,11,0.5)' : 'rgba(245, 158, 11, 0.4)'}`,
                  borderRadius: 6,
                  color: cleaningDots ? '#f59e0b' : '#3b82f6',
                  cursor: cleaningDots ? 'wait' : 'pointer',
                  fontWeight: 600
                }}
              >
                {cleaningDots ? (
                  <><i className="fas fa-sync-alt fa-spin" style={{ marginRight: 4 }} /> Cleaning...</>
                ) : (
                  <><i className="fas fa-broom" style={{ marginRight: 4 }} /> Clean Map Dots</>
                )}
              </button>
              {/* Clear All Dots Button — nuclear reset */}
              <button
                onClick={clearAllDots}
                disabled={cleaningDots || clearingDots}
                data-testid="clear-all-dots-btn"
                style={{
                  padding: '6px 14px',
                  fontSize: '0.75rem',
                  background: clearConfirmPending ? 'rgba(239, 68, 68, 0.4)' : clearingDots ? 'rgba(245, 158, 11, 0.3)' : 'rgba(239, 68, 68, 0.15)',
                  border: `1px solid ${clearConfirmPending ? 'rgba(239,68,68,0.8)' : clearingDots ? 'rgba(245,158,11,0.5)' : 'rgba(239, 68, 68, 0.4)'}`,
                  borderRadius: 6,
                  color: clearConfirmPending ? '#fff' : clearingDots ? '#f59e0b' : '#ef4444',
                  cursor: clearingDots ? 'wait' : 'pointer',
                  fontWeight: 600,
                  animation: clearConfirmPending ? 'pulse 1s infinite' : 'none'
                }}
              >
                {clearingDots ? (
                  <><i className="fas fa-sync-alt fa-spin" style={{ marginRight: 4 }} /> Clearing...</>
                ) : clearConfirmPending ? (
                  <><i className="fas fa-exclamation-triangle" style={{ marginRight: 4 }} /> Confirm Clear All</>
                ) : (
                  <><i className="fas fa-trash-alt" style={{ marginRight: 4 }} /> Clear All Dots</>
                )}
              </button>
              {(refreshProgress || cleanProgress) && (
                <span style={{ fontSize: '0.68rem', color: '#a1a1aa', maxWidth: 400 }}>{cleanProgress || refreshProgress}</span>
              )}
              {selectedCategories.length > 0 && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {selectedCategories.slice(0, 5).map((catId) => {
                    const cat = categories.find(c => c.id === catId);
                    return cat ? (
                      <span key={catId} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.75rem', color: '#a1a1aa' }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: getCategoryColor(catId) }}></span>
                        {cat.name}
                      </span>
                    ) : null;
                  })}
                  {selectedCategories.length > 5 && <span style={{ fontSize: '0.75rem', color: '#71717a' }}>+{selectedCategories.length - 5} more</span>}
                </div>
              )}
            </div>
          )}
        </div>
        {showMap && (
          <div>
          {/* Advanced Visualization Controls */}
          <SearchVizControls
            searchVizMode={searchVizMode}
            setSearchVizMode={setSearchVizMode}
            comparisonMode={comparisonMode}
            setComparisonMode={setComparisonMode}
            comparisonVizLeft={comparisonVizLeft}
            setComparisonVizLeft={setComparisonVizLeft}
            comparisonVizRight={comparisonVizRight}
            setComparisonVizRight={setComparisonVizRight}
            dotColorMode={dotColorMode}
            setDotColorMode={setDotColorMode}
          />

          {/* Window Overlay Banner */}
          {windowOverlay && (
            <div style={{ 
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'rgba(244,114,182,0.15)', border: '1px solid rgba(244,114,182,0.4)',
              borderRadius: 8, padding: '8px 14px', marginBottom: 10
            }}>
              <span style={{ color: '#047857', fontSize: '0.8rem', fontWeight: 600 }}>
                <i className="fas fa-layer-group" style={{ marginRight: 6 }} />
                Showing: "{windowOverlay.title}" — {windowOverlay.results.filter(r => r.latitude && r.longitude).length} of {windowOverlay.results.length} URLs with locations
              </span>
              <button onClick={() => setWindowOverlay(null)} 
                data-testid="clear-window-overlay-btn"
                style={{ 
                  background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.4)',
                  color: '#ef4444', padding: '3px 10px', borderRadius: 6, cursor: 'pointer',
                  fontSize: '0.72rem', fontWeight: 600
                }}>
                <i className="fas fa-times" style={{ marginRight: 4 }} /> Clear Overlay
              </button>
            </div>
          )}

          {/* Plotted Windows Info */}
          {Object.keys(plottedWindows).length > 0 && !windowOverlay && (
            <div style={{ 
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
              borderRadius: 8, padding: '6px 14px', marginBottom: 10
            }}>
              <span style={{ color: '#10b981', fontSize: '0.75rem', fontWeight: 600 }}>
                <i className="fas fa-check-square" style={{ marginRight: 4 }} />
                Plotted windows:
              </span>
              {Object.entries(plottedWindows).map(([wid, w]) => (
                <span key={wid} style={{ color: '#047857', fontSize: '0.7rem', background: 'rgba(253, 224, 71,0.15)', padding: '2px 8px', borderRadius: 4 }}>
                  {w.title} ({(w.results || []).filter(r => r.latitude && r.longitude).length} dots)
                </span>
              ))}
            </div>
          )}

          {/* Map container */}
          <div style={{ display: 'flex', gap: comparisonMode ? 10 : 20 }}>
            {/* Primary / Left Map */}
            <div style={{
              width: comparisonMode ? '50%' : '59.26%', height: 400, borderRadius: 12, overflow: 'hidden',
              border: `1px solid ${comparisonMode ? 'rgba(252, 211, 77,0.4)' : 'rgba(252, 211, 77, 0.3)'}`,
              flexShrink: 0, position: 'relative'
            }}>
              {comparisonMode && (
                <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 500, background: 'rgba(253, 251, 247,0.85)', padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(252, 211, 77,0.3)' }}>
                  <span style={{ color: '#047857', fontSize: '0.68rem', fontWeight: 700 }}>
                    {(comparisonVizLeft.charAt(0).toUpperCase() + comparisonVizLeft.slice(1))} View
                  </span>
                </div>
              )}
              <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: '100%', width: '100%' }} key={`map-${mapCenter[0]}-${mapCenter[1]}-${comparisonMode ? 'left' : 'single'}`}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                <SearchMapLayers
                  vizMode={comparisonMode ? comparisonVizLeft : searchVizMode}
                  results={filteredMapResults}
                  categoryColorMap={categoryColorMap}
                  categories={categories}
                  getCategoryColor={getCategoryColor}
                  dotColorMode={dotColorMode}
                  legendFilter={legendFilter}
                />
              </MapContainer>
              {/* Color Legend — Individual Dots and Fractal mode */}
              {['individual', 'fractal'].includes(comparisonMode ? comparisonVizLeft : searchVizMode) && (
                <MapColorLegend
                  results={filteredMapResults}
                  colorMode={dotColorMode}
                  categoryColorMap={categoryColorMap}
                  onFilterByItem={setLegendFilter}
                  activeFilter={legendFilter}
                />
              )}
            </div>

            {/* Right Comparison Map */}
            {comparisonMode && (
              <div style={{
                width: '50%', height: 400, borderRadius: 12, overflow: 'hidden',
                border: '1px solid rgba(16,185,129,0.4)', position: 'relative', flexShrink: 0
              }}>
                <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 500, background: 'rgba(253, 251, 247,0.85)', padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(16,185,129,0.3)' }}>
                  <span style={{ color: '#10b981', fontSize: '0.68rem', fontWeight: 700 }}>
                    {(comparisonVizRight.charAt(0).toUpperCase() + comparisonVizRight.slice(1))} View
                  </span>
                </div>
                <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: '100%', width: '100%' }} key={`map-compare-right-${mapCenter[0]}-${mapCenter[1]}`}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                  <SearchMapLayers
                    vizMode={comparisonVizRight}
                    results={filteredMapResults}
                    categoryColorMap={categoryColorMap}
                    categories={categories}
                    getCategoryColor={getCategoryColor}
                    dotColorMode={dotColorMode}
                    legendFilter={legendFilter}
                  />
                </MapContainer>
                {/* Color Legend — right pane Individual Dots mode */}
                {['individual', 'fractal'].includes(comparisonVizRight) && (
                  <MapColorLegend
                    results={filteredMapResults}
                    colorMode={dotColorMode}
                    categoryColorMap={categoryColorMap}
                    onFilterByItem={setLegendFilter}
                    activeFilter={legendFilter}
                  />
                )}
              </div>
            )}
            
            {/* Right side panel - Search Statistics (hidden in comparison mode) */}
            {!comparisonMode && (
            <SearchStatsPanel
              searchResults={searchResults}
              filteredMapResults={filteredMapResults}
              mapResults={mapResults}
              categories={categories}
              filteredResults={filteredResults}
              selectedCategories={selectedCategories}
              filterInfo={filterInfo}
              aggregation={aggregation}
              token={token}
            />
            )}
          </div>
          
          {mapResults.length === 0 && searchResults.length > 0 && (
            <p style={{ color: '#a1a1aa', fontSize: '0.85rem', marginTop: 10, textAlign: 'center' }}>
              💡 No results have location data. Results will appear on the map when they have latitude/longitude coordinates.
            </p>
          )}
          
          {filteredMapResults.length === 0 && mapResults.length > 0 && (
            <p style={{ color: '#f59e0b', fontSize: '0.85rem', marginTop: 10, textAlign: 'center' }}>
              🔍 No map results match your current filters. Try adjusting category or document type filters to see more results.
            </p>
          )}
          
          {/* === FILTER SECTION === */}
          <SearchMapFilters
            minQualityScore={minQualityScore}
            setMinQualityScore={setMinQualityScore}
            selectedDocTypes={selectedDocTypes}
            setSelectedDocTypes={setSelectedDocTypes}
            documentTypes={documentTypes}
            toggleDocType={toggleDocType}
            setShowFilteredResults={setShowFilteredResults}
          />
          </div>
        )}
      </div>

      {/* Categories & Sub-Categories below Map */}
      <CategoryTreePanel
        token={token}
        categories={categories}
        selectedCategories={selectedCategories}
        onCategoryClick={(catId) => {
          setSelectedCategories(prev =>
            prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
          );
        }}
      />
      
      {/* Protocol Performance Analytics Panel (appears when user clicks chart icon) */}
      {analyticsCategory && (
        <ProtocolPerformanceAnalytics
          categoryId={analyticsCategory.id}
          categoryName={analyticsCategory.name}
          onClose={() => setAnalyticsCategory(null)}
          showToast={showToast}
        />
      )}
      
      {/* ======= SEMANTIC SEARCH (Elasticsearch) ======= */}
      <SearchSemanticPanel
        showSemanticSearch={showSemanticSearch}
        setShowSemanticSearch={setShowSemanticSearch}
        esQuery={esQuery}
        setEsQuery={setEsQuery}
        esResults={esResults}
        esLoading={esLoading}
        esTotal={esTotal}
        esSyncing={esSyncing}
        onSearch={handleSemanticSearch}
        onSync={handleEsSync}
      />

      {/* Social Discovery Banner */}
      <SocialDiscoveryBanner variant="default" />
      
      {/* Letters to Evelyn — Book & Author Promotion */}
      <div className="card" style={{ marginBottom: 20, background: 'linear-gradient(135deg, rgba(245,158,11,0.06), rgba(253, 224, 71,0.06))', border: '1px solid rgba(245,158,11,0.2)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#047857', fontSize: '0.82rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
            <i className="fas fa-book" style={{ marginRight: 6 }} />
            Letters to Evelyn by John Selman
          </span>
          <a href="https://www.facebook.com/profile.php?id=61584845722433" target="_blank" rel="noopener noreferrer" data-testid="promo-facebook"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(245, 158, 11,0.15)', color: '#60a5fa', padding: '4px 12px', borderRadius: 20, textDecoration: 'none', fontSize: '0.72rem', fontWeight: 600, border: '1px solid rgba(245, 158, 11,0.2)' }}>
            <i className="fab fa-facebook" /> Facebook
          </a>
          <a href="https://twitter.com/AuthorSelman?s=09" target="_blank" rel="noopener noreferrer" data-testid="promo-twitter"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(245, 158, 11,0.15)', color: '#60a5fa', padding: '4px 12px', borderRadius: 20, textDecoration: 'none', fontSize: '0.72rem', fontWeight: 600, border: '1px solid rgba(245, 158, 11,0.2)' }}>
            <i className="fab fa-twitter" /> Twitter
          </a>
          <a href="https://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US/" target="_blank" rel="noopener noreferrer" data-testid="promo-amazon"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', padding: '4px 12px', borderRadius: 20, textDecoration: 'none', fontSize: '0.72rem', fontWeight: 600, border: '1px solid rgba(245,158,11,0.2)' }}>
            <i className="fab fa-amazon" /> Amazon
          </a>
          <a href="https://www.barnesandnoble.com/w/letters-to-evelyn-john-selman/1119415091?ean=2940162378570" target="_blank" rel="noopener noreferrer" data-testid="promo-bn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(16,185,129,0.15)', color: '#10b981', padding: '4px 12px', borderRadius: 20, textDecoration: 'none', fontSize: '0.72rem', fontWeight: 600, border: '1px solid rgba(16,185,129,0.2)' }}>
            <i className="fas fa-book-open" /> B&amp;N (Free eBook!)
          </a>
        </div>
      </div>
      
      {/* Smart Duplicate Detector */}
      <div className="card" style={{ marginBottom: 20, background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showDuplicates ? 12 : 0 }}>
          <button onClick={() => { setShowDuplicates(v => !v); if (!showDuplicates && duplicates.length === 0) loadDuplicates(); }}
            data-testid="toggle-duplicate-detector"
            style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.92rem', fontWeight: 600, padding: 0 }}>
            <i className="fas fa-clone" /> Smart Duplicate Detector
            <i className={`fas fa-chevron-${showDuplicates ? 'up' : 'down'}`} style={{ fontSize: '0.7rem' }} />
          </button>
          {showDuplicates && (
            <button onClick={loadDuplicates} disabled={dupLoading} data-testid="refresh-duplicates-btn"
              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: '0.72rem' }}>
              {dupLoading ? 'Scanning...' : 'Re-scan'}
            </button>
          )}
        </div>
        {showDuplicates && (
          <div>
            {duplicates.length === 0 && !dupLoading && (
              <p style={{ color: '#71717a', fontSize: '0.8rem', textAlign: 'center', padding: 16 }}>No duplicates found. Your URL database is clean!</p>
            )}
            {duplicates.map((dup, i) => (
              <div key={i} data-testid={`duplicate-group-${i}`} style={{
                padding: '8px 12px', marginBottom: 4, borderRadius: 6, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.1)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#fca5a5', fontSize: '0.78rem', fontWeight: 600 }}>{dup.normalized_url}</span>
                  <span style={{ color: '#ef4444', fontSize: '0.65rem', background: 'rgba(239,68,68,0.15)', padding: '2px 8px', borderRadius: 10 }}>
                    {dup.count}x
                  </span>
                </div>
                <div style={{ marginTop: 4 }}>
                  {dup.entries?.slice(0, 3).map((e, j) => (
                    <div key={j} style={{ color: '#a1a1aa', fontSize: '0.7rem', padding: '1px 0', display: 'flex', gap: 8 }}>
                      <span style={{ color: '#71717a' }}>{e.article_type}</span>
                      <span>{e.title?.slice(0, 60)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Discover What's Trending */}
      <DiscoverTrending onSearchQuery={(q) => setSearchQuery(q)} />

      {/* Chat Module - position: below_trending */}
      {chatModulePosition === 'below_trending' && <CompactChatCollabModule showToast={showToast} />}

      {/* Install App Banner */}
      <InstallAppButton variant="banner" />

      {/* Chat Module - position: below_install */}
      {chatModulePosition === 'below_install' && <CompactChatCollabModule showToast={showToast} />}

      {/* Chat Module - position: above_windows (default) */}
      {(chatModulePosition === 'above_windows' || !chatModulePosition) && <CompactChatCollabModule showToast={showToast} />}

      {/* Search Result Windows - Max 60 closeable paginated windows */}
      <SearchWindowManager
        windows={searchWindows}
        onCloseWindow={closeSearchWindow}
        onResultClick={(result, cat) => {
          showToast(`Clicked: ${cat || result.title}`, 'info');
        }}
        token={token}
        showToast={showToast}
        autoUpdateEnabled={autoUpdateEnabled}
        setAutoUpdateEnabled={setAutoUpdateEnabled}
        maxUrlsPerWindow={user?.urls_per_window || 600}
        onChangeMaxUrls={(newMax) => {
          // Save to user preferences on the backend
          const x = new XMLHttpRequest();
          x.open('PUT', `${API}/user/preferences`, true);
          x.setRequestHeader('Authorization', `Bearer ${token}`);
          x.setRequestHeader('Content-Type', 'application/json');
          x.onload = function() {
            if (x.status === 200) showToast(`Max URLs per window set to ${newMax}`, 'success');
          };
          x.send(JSON.stringify({ urls_per_window: newMax }));
        }}
        activeFilters={{
          selectedDocTypes,
          certificationFilters,
          educatedIndividualFilters,
          minWordCount,
          selectedCategories,
          categories,
          aggregation,
          searchQuery: searchQuery
        }}
        onRevertToSnapshot={(snapshot) => {
          if (snapshot.selectedDocTypes) setSelectedDocTypes(snapshot.selectedDocTypes);
          if (snapshot.certificationFilters) setCertificationFilters(snapshot.certificationFilters);
          if (snapshot.educatedIndividualFilters) setEducatedIndividualFilters(snapshot.educatedIndividualFilters);
          if (snapshot.minWordCount !== undefined) setMinWordCount(snapshot.minWordCount);
          if (snapshot.selectedCategories) setSelectedCategories(snapshot.selectedCategories);
          if (snapshot.aggregation) setAggregation(snapshot.aggregation);
          if (snapshot.searchQuery !== undefined) setSearchQuery(snapshot.searchQuery);
        }}
        miniatureAnchorRef={miniatureAnchorRef}
      />

      {/* ===== UNIFIED RESULTS: Filtered + Search Results Side-by-Side ===== */}
      <div data-testid="unified-results-section" className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#334155' }}>
            <i className="fas fa-database" style={{ marginRight: 6, color: '#FDE047' }} />
            Results Dashboard — {filteredResults.length} filtered / {searchResults.length} total
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              data-testid="toggle-filtered-results"
              onClick={() => setShowFilteredResults(v => !v)}
              style={{
                padding: '3px 10px', borderRadius: 6, cursor: 'pointer',
                fontSize: '0.62rem', fontWeight: 600,
                border: '1px solid rgba(244,114,182,0.3)',
                background: showFilteredResults ? 'rgba(244,114,182,0.15)' : 'transparent',
                color: '#047857'
              }}
            >
              {showFilteredResults ? 'Hide Filtered' : 'Show Filtered'}
            </button>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: showFilteredResults ? '1fr 1fr' : '1fr',
          gap: 8
        }}>
          {/* LEFT: Filtered Results */}
          {showFilteredResults && (
            <div data-testid="filtered-results-panel" style={{
              maxHeight: 400, overflowY: 'auto', padding: 6,
              background: 'rgba(244,114,182,0.03)', borderRadius: 8,
              border: '1px solid rgba(244,114,182,0.1)'
            }}>
              <div style={{ fontSize: '0.58rem', color: '#047857', fontWeight: 700, marginBottom: 4, position: 'sticky', top: 0, background: 'rgba(20,10,35,0.95)', padding: '3px 6px', borderRadius: 4, zIndex: 1 }}>
                FILTERED ({filteredResults.length})
              </div>
              {filteredResults.length === 0 ? (
                <div style={{ color: '#71717a', fontSize: '0.56rem', textAlign: 'center', padding: 16 }}>No results match current filters</div>
              ) : (
                filteredResults.map((result, idx) => (
                  <div key={result.id || idx} data-testid={`filtered-result-${idx}`} style={{
                    padding: '3px 6px', borderBottom: '1px solid rgba(255, 255, 255, 0.85)',
                    fontSize: '0.54rem', lineHeight: 1.35, color: '#d4d4d8',
                    cursor: 'pointer', userSelect: 'text', WebkitUserSelect: 'text'
                  }}
                    onClick={() => result.url && window.open(result.url, '_blank')}
                  >
                    <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.56rem' }}>
                      {result.title || result.url || 'Untitled'}
                    </div>
                    <div style={{ color: '#71717a', fontSize: '0.5rem', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <span>{result.article_type || 'Unknown'}</span>
                      {result.root_domain && <span style={{ color: '#60a5fa' }}>{result.root_domain}</span>}
                      {result.categories?.length > 0 && <span style={{ color: '#047857' }}>{result.categories.join(', ')}</span>}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* RIGHT: All Search Results */}
          <div data-testid="search-results-panel" style={{
            maxHeight: 400, overflowY: 'auto', padding: 6,
            background: 'rgba(253, 224, 71,0.03)', borderRadius: 8,
            border: '1px solid rgba(253, 224, 71,0.1)'
          }}>
            <div style={{ fontSize: '0.58rem', color: '#FDE047', fontWeight: 700, marginBottom: 4, position: 'sticky', top: 0, background: 'rgba(20,10,35,0.95)', padding: '3px 6px', borderRadius: 4, zIndex: 1 }}>
              ALL RESULTS ({searchResults.length})
            </div>
            {searchResults.length === 0 ? (
              <div style={{ color: '#71717a', fontSize: '0.56rem', textAlign: 'center', padding: 16 }}>No search results yet. Use the search bar above to find URLs.</div>
            ) : (
              searchResults.map((result, idx) => (
                <div key={result.id || idx} data-testid={`search-result-${idx}`} style={{
                  padding: '3px 6px', borderBottom: '1px solid rgba(255, 255, 255, 0.85)',
                  fontSize: '0.54rem', lineHeight: 1.35, color: '#d4d4d8',
                  cursor: 'pointer', userSelect: 'text', WebkitUserSelect: 'text'
                }}
                  onClick={() => result.url && window.open(result.url, '_blank')}
                >
                  <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.56rem' }}>
                    {result.title || result.url || 'Untitled'}
                  </div>
                  <div style={{ color: '#71717a', fontSize: '0.5rem', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <span>{result.article_type || 'Unknown'}</span>
                    {result.root_domain && <span style={{ color: '#60a5fa' }}>{result.root_domain}</span>}
                    {result.categories?.length > 0 && <span style={{ color: '#047857' }}>{result.categories.join(', ')}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(420px, 540px) 1fr', gap: 16 }}>
        {/* Categories Panel - Wider, central, scrollable */}
        <div className="card" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {/* Microfont instruction */}
          <div style={{ padding: '3px 8px', color: '#555', fontSize: '0.46rem', borderBottom: '1px solid rgba(255, 255, 255, 0.85)', marginBottom: 8 }}>
            Click a category name to open its URLs as a Collection Window above. Check the box to include in searches. Double-click sidebar nav to rename pages.
          </div>
          {/* Tab Navigation */}
          <div style={{ 
            display: 'flex', 
            gap: 0, 
            marginBottom: 15,
            background: '#475569',
            borderRadius: 10,
            padding: 4
          }}>
            <button
              onClick={() => setCategoryTab('my')}
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: 8,
                border: 'none',
                background: categoryTab === 'my' 
                  ? 'linear-gradient(135deg, #FEF08A, #FDE68A)' 
                  : 'transparent',
                color: categoryTab === 'my' ? '#fff' : '#a1a1aa',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: categoryTab === 'my' ? 600 : 400,
                transition: 'all 0.2s'
              }}
              data-testid="tab-my-categories"
            >
              📂 My Categories
            </button>
            <button
              onClick={() => setCategoryTab('friends')}
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: 8,
                border: 'none',
                background: categoryTab === 'friends' 
                  ? 'linear-gradient(135deg, #FDE047, #FCD34D)' 
                  : 'transparent',
                color: categoryTab === 'friends' ? '#fff' : '#a1a1aa',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: categoryTab === 'friends' ? 600 : 400,
                transition: 'all 0.2s'
              }}
              data-testid="tab-friends-protocols"
            >
              Friends'
            </button>
            <button
              onClick={() => setCategoryTab('marketplace')}
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: 8,
                border: 'none',
                background: categoryTab === 'marketplace' 
                  ? 'linear-gradient(135deg, #FCD34D, #4f46e5)' 
                  : 'transparent',
                color: categoryTab === 'marketplace' ? '#fff' : '#a1a1aa',
                cursor: 'pointer',
                fontSize: '0.82rem',
                fontWeight: categoryTab === 'marketplace' ? 600 : 400,
                transition: 'all 0.2s'
              }}
              data-testid="tab-protocol-marketplace"
            >
              <i className="fas fa-store" style={{ marginRight: 4, fontSize: '0.7rem' }} />
              Shared
            </button>
          </div>
          
          {/* My Categories Tab */}
          {categoryTab === 'my' && (
            <>
              <h3 style={{ marginBottom: 15, color: '#047857', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>📂 My Categories</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: '0.75rem', color: '#a1a1aa', fontWeight: 'normal' }}>
                    {categories.length} categories
                  </span>
                  <button onClick={() => {
                    setNewCategory({ name: '', protocol: '', parent_id: null, privacy: 'public' });
                    setShowCategoryModal(true);
                  }}
                    data-testid="add-category-btn"
                    style={{
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      border: 'none', borderRadius: 8, width: 30, height: 30,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', fontSize: '1.1rem', color: '#0F172A',
                      boxShadow: '0 2px 8px rgba(16,185,129,0.3)'
                    }} title="Add new category">
                    <i className="fas fa-plus" style={{ fontSize: '0.8rem' }} />
                  </button>
                </div>
              </h3>
              
              {/* Collapsible Category Tree */}
              <CollapsibleCategoryTree
                categories={categories}
                selectedCategories={selectedCategories}
                onToggleSelect={toggleCategorySelection}
                onSelectAll={() => setSelectedCategories(categories.map(c => c.id))}
                onDeselectAll={() => setSelectedCategories([])}
                onCategoryTitleClick={handleCategoryTitleClick}
                onCategoryCopied={(data) => {
                  // Refresh categories after copy
                  fetchCategories();
                }}
                onEdit={(cat) => {
                  setEditingCategory(cat);
                  setEditCategoryName(cat.name || '');
                  setEditProtocol(cat.protocol || '');
                  setEditPrivacy(cat.privacy || 'private');
                }}
                onDelete={(catId) => deleteCategory(catId)}
                isOwner={true}
                compact={true}
                token={token}
                showToast={showToast}
              />
            </>
          )}
          
          {/* Friends' Protocols Tab */}
          {categoryTab === 'friends' && (
            <FriendsProtocolBrowser
              showToast={showToast}
              onProtocolCopied={(newCategory) => {
                setCategories(prev => [...prev, newCategory]);
                showToast('Protocol copied! Switch to "My Categories" to see it.', 'success');
              }}
              onResultsLoaded={(results) => {}}
            />
          )}
          
          {/* Protocol Sharing Marketplace Tab */}
          {categoryTab === 'marketplace' && (
            <ProtocolSharingMarketplace
              showToast={showToast}
              onProtocolImported={(newCat) => {
                fetchCategories();
                showToast(`Imported "${newCat.name}"! Switch to "My Categories" to use it.`, 'success');
              }}
            />
          )}
        </div>

        {/* Batch Manager */}
        <BatchManager
          batches={batches}
          showBatchManager={showBatchManager}
          setShowBatchManager={setShowBatchManager}
          onFetchBatches={fetchBatches}
          onDeleteBatch={deleteBatch}
          lastBatchId={lastBatchId}
        />

        {/* CLICKED CATEGORY RESULTS - Displayed in ultra-compact rows */}
        {Object.keys(clickedCategoryResults).length > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(20, 10, 40, 0.9))',
            borderRadius: 12,
            padding: 15,
            marginBottom: 10,
            border: '1px solid rgba(16, 185, 129, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10
            }}>
              <h4 style={{ 
                color: '#10b981', 
                margin: 0, 
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                <span>📄</span>
                Category Results ({combinedClickedResults.length})
                <span style={{ fontSize: '0.7rem', color: '#71717a' }}>
                  from {Object.keys(clickedCategoryResults).length} categories
                </span>
              </h4>
              <button
                onClick={clearClickedCategoryResults}
                style={{
                  background: 'rgba(239, 68, 68, 0.2)',
                  border: 'none',
                  color: '#ef4444',
                  padding: '4px 10px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: '0.75rem'
                }}
                data-testid="clear-clicked-results"
              >
                Clear All
              </button>
            </div>
            
            {/* Category badges showing which are loaded */}
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 6, 
              marginBottom: 12,
              paddingBottom: 12,
              borderBottom: '1px solid rgba(253, 224, 71, 0.2)'
            }}>
              {Object.entries(clickedCategoryResults).map(([catId, data]) => (
                <span
                  key={catId}
                  onClick={() => handleCategoryTitleClick({ id: catId, name: data.name })}
                  style={{
                    background: data.loading ? 'rgba(245, 158, 11, 0.2)' : 'rgba(253, 224, 71, 0.2)',
                    color: data.loading ? '#f59e0b' : '#047857',
                    padding: '3px 8px',
                    borderRadius: 12,
                    fontSize: '0.7rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                >
                  {data.name}
                  <span style={{ 
                    background: 'rgba(0,0,0,0.3)', 
                    padding: '1px 4px', 
                    borderRadius: 8,
                    fontSize: '0.65rem'
                  }}>
                    {data.loading ? '...' : data.results?.length || 0}
                  </span>
                  <span style={{ color: '#ef4444', marginLeft: 2 }}>×</span>
                </span>
              ))}
            </div>

            {/* Ultra-Compact Results Display with Drag & Drop */}
            <div style={{ 
              maxHeight: '400px', 
              overflowY: 'auto',
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: 8,
              padding: 8
            }}>
              {/* Drag hint */}
              {orderedResults.length > 1 && (
                <div style={{
                  padding: '6px 10px',
                  marginBottom: 8,
                  background: 'rgba(253, 224, 71, 0.1)',
                  borderRadius: 6,
                  fontSize: '0.7rem',
                  color: '#047857',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}>
                  <span>↕️</span> Drag results to reorder priority
                </div>
              )}
              
              {orderedResults.length === 0 ? (
                <div style={{ 
                  padding: 20, 
                  textAlign: 'center', 
                  color: '#71717a' 
                }}>
                  <p>Loading results...</p>
                </div>
              ) : (
                orderedResults.map((result, idx) => (
                  <div
                    key={result.id || result._id || idx}
                    draggable
                    onDragStart={(e) => handleDragStart(e, result, idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, idx)}
                    onDragEnd={handleDragEnd}
                    style={{
                      background: dragOverIndex === idx ? 'rgba(253, 224, 71, 0.2)' : 'transparent',
                      borderTop: dragOverIndex === idx ? '2px solid #FDE047' : '2px solid transparent',
                      transition: 'all 0.15s',
                      cursor: 'grab',
                      opacity: draggedResult?.index === idx ? 0.5 : 1
                    }}
                  >
                    <CompactResultRow
                      result={result}
                      token={token}
                      showToast={showToast}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Search Engine Access Results */}
        <SearchEngineResultsPanel
          searchEngineResults={searchEngineResults}
          setSearchEngineResults={setSearchEngineResults}
          token={token}
          showToast={showToast}
        />
      </div>

      {/* Large Book Advertisement - Prominently displayed */}
      <LargeBookAd variant={1} />
      <BookOfTheWeekCarousel />
      <BannerBookAd variant={2} />

      {/* Create Category Modal */}
      <CreateCategoryModal
        show={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        newCategory={newCategory}
        setNewCategory={setNewCategory}
        categories={categories}
        onCreate={createCategory}
      />

      {/* Edit Category Modal */}
      <EditCategoryModal
        editingCategory={editingCategory}
        onClose={() => setEditingCategory(null)}
        editCategoryName={editCategoryName}
        setEditCategoryName={setEditCategoryName}
        editProtocol={editProtocol}
        setEditProtocol={setEditProtocol}
        editPrivacy={editPrivacy}
        setEditPrivacy={setEditPrivacy}
        user={user}
        onSave={saveProtocol}
        onDelete={deleteCategory}
        onCleanCategory={cleanCategory}
      />
      
      {/* Share Category Modal */}
      <ShareCategoryModal
        category={shareCategory}
        isOpen={!!shareCategory}
        onClose={() => setShareCategory(null)}
        token={token}
        showToast={showToast}
      />

      {/* Content Dump Modal */}
      <ContentDumpModal
        isOpen={showContentDump}
        onClose={() => setShowContentDump(false)}
        searchQuery={searchQuery}
      />
    </div>
  );
};

export default UltimateSearchPage;
